import { useState, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiGet, apiPut } from '@/utils/api';
import { displayDateTime } from '@/utils/dateDisplay';
import { formatDistanceToNow } from 'date-fns';

// 🔴 DSH-040: THE FIELDS THE API ACTUALLY SENDS. This declared `message` and
// `link`; the list returns `body`, and (until the server learned to work it
// out) no link at all — so every row showed only its title, three desk
// bookings were three identical rows, and a click went nowhere.
interface NotificationItem {
  id: string;
  title: string;
  body: string | null;
  is_read: boolean;
  created_at: string;
  /** In-app path, from backend services/notification_service.notification_link. */
  link?: string | null;
}

//: The list route's own page size, and its ceiling (limit le=100).
const PAGE = 20;
const MAX = 100;

export default function NotificationCentre() {
  const [isOpen, setIsOpen] = useState(false);
  // Only the newest 20 were ever listed, with no way to the rest. "Show
  // older" widens the window a page at a time, to the route's ceiling.
  const [limit, setLimit] = useState(PAGE);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['notifications', limit],
    queryFn: () => apiGet<{
      notifications: NotificationItem[];
      unread_count: number;
    }>(`/notifications/?limit=${limit}`),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => apiPut(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: () => apiPut('/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unread_count ?? 0;
  // A full page back means there may be more behind it.
  const mayHaveOlder = notifications.length >= limit;

  const open = (n: NotificationItem) => {
    if (!n.is_read) markRead.mutate(n.id);
    if (n.link) {
      setIsOpen(false);
      navigate(n.link);
    }
  };

  return (
    <>
      {/* Bell button with badge (Feature #15: WCAG aria-label) */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="relative rounded-lg p-2 text-content-muted transition-colors hover:bg-surface-muted hover:text-content-secondary"
        aria-label="Open notifications"
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-700 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Slide-out panel */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-scrim/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto fixed inset-y-0 right-0 w-full max-w-sm">
                  <div className="flex h-full flex-col bg-surface shadow-xl">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-line px-4 py-4">
                      <Dialog.Title className="text-lg font-semibold text-content">
                        Notifications
                      </Dialog.Title>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={() => markAllRead.mutate()}
                            className="text-xs font-medium text-brand-700 hover:text-brand-700"
                          >
                            Mark all read
                          </button>
                        )}
                        <button
                          onClick={() => setIsOpen(false)}
                          className="rounded-lg p-1 text-content-subtle hover:bg-surface-muted"
                         aria-label="Close notifications">
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    {/* Notifications list */}
                    <div className="flex-1 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-content-subtle">
                          <BellIcon className="h-12 w-12 mb-3" />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-line-subtle">
                          {notifications.map((n) => (
                            <button
                              key={n.id}
                              onClick={() => open(n)}
                              className={`w-full px-4 py-3 text-left transition-colors hover:bg-surface-subtle ${
                                !n.is_read ? 'bg-brand-50/30' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {!n.is_read && (
                                  <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-700" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className={`text-sm ${!n.is_read ? 'font-semibold text-content' : 'text-content-secondary'}`}>
                                    {n.title}
                                  </p>
                                  {n.body && (
                                    <p className="mt-0.5 text-xs text-content-muted line-clamp-2">{n.body}</p>
                                  )}
                                  <p
                                    className="mt-1 text-[10px] text-content-subtle"
                                    title={displayDateTime(n.created_at)}
                                  >
                                    {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                          {mayHaveOlder && limit < MAX && (
                            <div className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => setLimit((l) => Math.min(l + PAGE, MAX))}
                                className="text-xs font-medium text-brand-700 hover:underline"
                              >
                                Show older notifications
                              </button>
                            </div>
                          )}
                          {mayHaveOlder && limit >= MAX && (
                            <p className="px-4 py-3 text-center text-xs text-content-subtle">
                              Showing your latest {MAX} notifications.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Employee rebuild C11: the whole inbox, by category. */}
                    <div className="border-t border-line px-4 py-3 text-center">
                      <Link
                        to="/notifications"
                        onClick={() => setIsOpen(false)}
                        className="text-sm font-medium text-brand-700 hover:underline"
                        data-testid="see-all-notifications"
                      >
                        See all notifications
                      </Link>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
